// The hotbar's model: what you could make, and where it could land (PLAN-UI
// §28.3, U4).
//
// Forging used to be a *machine's* menu — select a rook, read its recipes off
// the shelf, click one. That asked the player to find the foundry before it
// would tell them what an army costs, and since U-R the bill is split between
// every foundry touching the landing square, so no single machine's list is
// the truth any more: two rooks each holding half a pawn each showed a refusal
// and between them could pay.
//
// So the question is turned round. **Arm a piece type, and every square any
// coalition of yours could pay for lights up.** The union over your foundries
// is a fact about the board rather than about a selection, which is what lets
// it live in persistent chrome — and `leads` is how a lit square gets back to
// a machine, since the reducer still wants a lead for a build and takes one
// for a forge.
//
// Pure, like `pieceBadges.ts`: no React, no DOM, so the arithmetic can be
// tested without a renderer.

import {
  BISHOP,
  type Color,
  KING,
  KNIGHT,
  makePiece,
  PAWN,
  pieceColor,
  type PieceType,
  QUEEN,
  ROOK
} from "src/engine/rules/board";
import type { GameState } from "src/engine/rules/commands";
import { forgeRefusal } from "src/engine/rules/clock";
import {
  bindingRoyal,
  clonePosition,
  pieceAt,
  type Position,
  setPiece
} from "src/engine/rules/position";
import { isAttackedByEnemyOf } from "src/engine/rules/chess/attacks";
import { isGameOver } from "src/engine/rules/chess/status";
import { hasPower } from "src/engine/rules/campaign/powers";
import {
  billFor,
  materialName,
  type MaterialLine,
} from "src/engine/rules/materials";
import { MINTABLE } from "src/engine/rules/rules";
import { fundableSquares, fundersFor } from "src/engine/rules/industry/coalition";
import { isFoundryAt, mintableFor } from "src/engine/rules/industry/machines";
import {
  ALTAR,
  ARMORY,
  BATTERY,
  CABLING_TOWER,
  COAL_GENERATOR,
  FIRM_WALL,
  KEYSTONE,
  NUCLEAR_GENERATOR,
  REINFORCED_WALL,
  RELIC,
  SIMPLE_WALL,
  STORE,
  type StructureKind,
  TRACK,
  TRAIN_CAR,
  TRAIN_ENGINE,
  TURRET,
  WELL,
  FUSION_CHAMBER,
  SHIELD_GENERATOR,
  ARTISAN_PILLAR,
  KINETIC_TURRET,
  CANNON_TURRET,
  PROPAGANDA_TOWER,
  HOUSE_OF_CARDS,
  ENDURANCE_MONUMENT,
  FIEND_FOUNTAIN,
  LEGION_TOWER,
  DRONE_WAREHOUSE,
  EMPOWER_TOWER
} from "src/engine/rules/industry/state";
import { isTurretKind } from "src/engine/rules/industry/turret";
import { BUILD_SAYS } from "./refusals";
import {
  type BuildError,
  BUILD_KINDS,
  buildSquares,
  canBuild,
  isObstructionKind,
  reservedSquares,
  structureBill
} from "src/engine/rules/industry/structures";

/**
 * One row of a price, as the tile draws it: a number and a colour.
 *
 * **The words are the title's job.** A 66px tile cannot hold "18 crucible
 * steel · 1 electrum" without wrapping to four lines, and the readouts already
 * settled the question for the stock pill — the swatch is the label, hover
 * says the name (designer ruling, U3 review). So the model hands over the bill
 * rows themselves and the tile draws a count against `UNIT_TONE`.
 */
export interface TileRow {
  count: number;
  line: MaterialLine;
  grade: number;
}

/**
 * What each rank *does* in the factory, in one line (designer ruling, U6
 * review).
 *
 * The hover card is name, price and this — and the third line is the one that
 * was never anywhere: a bar that prices a bishop at four iron says nothing
 * about a bishop being an oven, which is the only reason anybody would want
 * one. So it is the piece's industrial job rather than its chess move, which
 * the board itself teaches: what a knight does *here* is turn a pawn round.
 *
 * Keyed like `PIECE_NAMES`, and total for the same reason — a rank with no
 * sentence is a card with a hole in it, and `hotbar.test.ts` fails on one.
 */
export const PIECE_ROLE: Record<PieceType, string> = {
  [PAWN]: "Mines the heap it stands on and loads a belt beside it.",
  [KNIGHT]: "Turns a pawn to face a new way.",
  [BISHOP]: "An oven: material goes in and comes out a grade higher.",
  [ROOK]: "A foundry: forges new pieces, and aims the pawns it mints.",
  [QUEEN]: "Sweeps up the material on every square she crosses.",
  [KING]: "Refines and forges — and is the piece you cannot lose."
};

/** What each building does, in one line. The structures' half of `PIECE_ROLE`. */
export const STRUCTURE_ROLE: Record<StructureKind, string> = {
  [STORE]: "Holds material the network has nowhere better to put.",
  [TURRET]: "Laser: 1 damage to random enemies within 3, twice a round; a fuel point a shot.",
  [ALTAR]: "Takes a piece of yours apart, and eats a body captured beside it.",
  [RELIC]: "One more move every turn, and a factory tick with it.",
  [KEYSTONE]: "The Targeting Array: lets you build turrets; each one more makes them stronger.",
  [WELL]: "10 fuel points a tick; with the Scepter, play an enemy's turn every 5 rounds.",
  [SIMPLE_WALL]: "Blocks every move that does not jump, and line of sight. 5 HP.",
  [FIRM_WALL]: "A wall that ranged fire cannot hurt. Can go over a Simple Wall.",
  [REINFORCED_WALL]: "10 HP and immune to ranged fire. Goes over a Firm Wall, by an Armory.",
  [ARMORY]: "Pieces within 2 of it may be upgraded. One must stand to upgrade at all.",
  [COAL_GENERATOR]: "Burns coal for 5 fuel points a tick, to ovens within 4.",
  [NUCLEAR_GENERATOR]: "10 fuel points a tick, no coal. Explodes for 10 within 4 if destroyed.",
  [BATTERY]: "Stores up to 50 fuel points across turns, and hands them out within 4.",
  [CABLING_TOWER]: "Carries any power source's reach 4 squares further.",
  [TRACK]: "Rail for trains. Plowed by enemy moves. 1 HP.",
  [TRAIN_ENGINE]: "Goes on your track. Burns 3 coal to run up to 5 squares a tick, forward only.",
  [TRAIN_CAR]: "Goes on your track behind an engine. Carries 2 of your pieces.",
  [FUSION_CHAMBER]: "An Armory; with the Signet of Union, fuses two pieces within 2 of it.",
  [SHIELD_GENERATOR]: "With 5 fuel points a round, every piece of yours in reach takes 1 less damage.",
  [ARTISAN_PILLAR]: "+1 HP and faster healing for all your pieces, Monarchs reach 2 further. Losing it costs dearly.",
  [KINETIC_TURRET]: "2 damage down a 5-deep cone, twice a round, nearest first; a marble a shot, a fuel point a round.",
  [CANNON_TURRET]: "5 damage down a 3-deep cone, once a round, farthest first; a steel a shot, a fuel point a round.",
  [PROPAGANDA_TOWER]: "Stoke the Forges: +1 Off-Turn Production point — extra ticks each round per enemy faction. One only.",
  [HOUSE_OF_CARDS]: "My Deck Runith Over: its Monarch hits 2 harder; a card reward every 20 of your factory ticks.",
  [ENDURANCE_MONUMENT]: "Yield Not: its Monarch +10 armor; your obstructions +3 HP, your other buildings +1.",
  [FIEND_FOUNTAIN]: "Flow Faster: Factory Flow no longer binds you — forge and move in one turn.",
  [LEGION_TOWER]: "Legion Mandate: +1 Army Logistics — each Power Move brings one more Fodder Move. Two at most.",
  [DRONE_WAREHOUSE]: "Drone-Assisted Warehouse: built over your store, holds 500 and supplies anything of yours within 5 that runs short.",
  [EMPOWER_TOWER]: "Empower Tower: every two in reach of your fuel source grant one extra move a turn near it — 3 fuel for fodder, 10 for others."
};

/**
 * Which builder's refusal a greyed tile says (§31 S4–S6): the one nearest to
 * working. A missing Armory outranks a missing wall outranks reach, then purse.
 */
const BUILD_ERROR_RANK: readonly BuildError[] = [
  "no-armory",
  "needs-firm-wall",
  "needs-store",
  "out-of-armory-reach",
  "too-poor",
  "no-room"
];

/** One forge tile: a rank, its price, and every square it could land on. */
export interface ForgeTile {
  type: PieceType;
  /** The price line — "2 iron", "6 silver · 2 stone". One phrase per bill row. */
  caption: string;
  /** The same price, as the tile draws it. `caption` is what the title says. */
  rows: TileRow[];
  /**
   * Every square a forge of this type could be paid for, over *every* foundry
   * of the side, ascending and deduplicated.
   *
   * Computed whatever `reason` says, because a greyed tile still has to be
   * able to explain itself and the count is part of the explanation.
   */
  squares: number[];
  /** Which foundry leads at each square: the lowest one that could. */
  leads: ReadonlyMap<number, number>;
  /**
   * How many foundries chip in, at the squares where it is more than one.
   *
   * Only the shared squares, because the interesting mark is "this one is
   * being split" — a map with a 1 on every ordinary square would be a
   * decoration on the whole board.
   */
  shared: ReadonlyMap<number, number>;
  /** The army forges this rank at all — `mintableFor`. Off for the game, not the turn. */
  forgeable: boolean;
  /** Null when the tile may be armed; otherwise what the hover card says. */
  reason: string | null;
  /** What this rank does in the factory, one line. `PIECE_ROLE`. */
  role: string;
}

/** One build tile: a structure kind, its price, and where it could stand. */
export interface BuildTile {
  kind: StructureKind;
  caption: string;
  rows: TileRow[];
  squares: number[];
  leads: ReadonlyMap<number, number>;
  reason: string | null;
  /** What this building does, one line. `STRUCTURE_ROLE`. */
  role: string;
}

/** A structure token in hand, with how many of that kind there are. */
export interface TokenTile {
  kind: StructureKind;
  count: number;
  /** What this building does, one line. `STRUCTURE_ROLE`. */
  role: string;
}

export interface HotbarModel {
  forge: ForgeTile[];
  build: BuildTile[];
  tokens: TokenTile[];
}

/**
 * Every foundry of `side` on the board, ascending.
 *
 * Ascending because `leads` is "the lowest foundry that could", and a packed
 * square sorts rank-then-file — the codebase's canonical order, and the one
 * `fundersFor` already divides a bill in. `cellList` is already in it.
 */
function foundriesOf(state: GameState, side: Color): number[] {
  const out: number[] = [];
  for (const sq of state.position.cellList) {
    const piece = pieceAt(state.position, sq);
    if (!piece || pieceColor(piece) !== side) continue;
    if (isFoundryAt(state.position, state.industry, sq)) out.push(sq);
  }
  return out;
}

/** Every piece of `side`, ascending — each one a builder (§31 S3). */
function buildersOf(state: GameState, side: Color): number[] {
  const out: number[] = [];
  for (const sq of state.position.cellList) {
    const piece = pieceAt(state.position, sq);
    if (piece && pieceColor(piece) === side) out.push(sq);
  }
  return out;
}

/**
 * While `side` is in check, which of these squares would *break* it.
 *
 * `forge` and `build` both end in `requireKingSafe` (`rules/reduce.ts`): they
 * spend the turn without being moves, so they never went through the legality
 * filter, and the ordinary rule of chess applies to them — you may not end
 * your turn in check. So a lit square that does not block the line is a square
 * the reducer refuses, which breaks the one promise the lighting makes.
 *
 * **Only while in check, and that is not an optimisation.** A piece *added* to
 * the board can never expose a king — it can only stand in the way of
 * something — so out of check every square passes and the clone per square
 * would be paid for nothing.
 *
 * `bindingRoyal` rather than `kings[side]`, so a side holding a spare is
 * unconstrained here exactly as it is in the reducer: one reading of "am I in
 * check", not two.
 *
 * Null means "not in check" — nothing to filter.
 */
function checkFilter(
  position: Position,
  side: Color
): ((at: number, type: PieceType) => boolean) | null {
  const king = bindingRoyal(position, side);
  if (!(king >= 0)) return null;
  if (!isAttackedByEnemyOf(position, king, side)) return null;
  // A square is asked once for the whole bar, however many foundries reach it
  // and whatever tile is asking: the added piece is only ever a *blocker*, so
  // its rank cannot change whether the line is cut, and the clone is the
  // expensive half.
  const seen = new Map<number, boolean>();
  return (at, type) => {
    const was = seen.get(at);
    if (was !== undefined) return was;
    const clone = clonePosition(position);
    setPiece(clone, at, makePiece(side, type));
    const breaks = !isAttackedByEnemyOf(clone, king, side);
    seen.set(at, breaks);
    return breaks;
  };
}

/** A price said twice: the sentence for the title, the rows for the tile. */
interface Price {
  caption: string;
  rows: TileRow[];
}

/**
 * The price of a recipe as one line.
 *
 * The bill as it can actually be paid — `billFor`, not `mintBill` — so a row
 * charged in a material nobody can obtain is not quoted at a player who will
 * never be asked for it.
 */
function billPrice(state: GameState, type: PieceType): Price {
  const bill = billFor(type);
  return {
    caption: bill
      .map((row) => `${row.count} ${materialName(row.line, row.grade).toLowerCase()}`)
      .join(" · "),
    rows: bill.map((row) => ({ count: row.count, line: row.line, grade: row.grade }))
  };
}

/**
 * What the whole bar offers this side, priced and lit.
 *
 * Everything is derived from the board every call and nothing is cached here:
 * the caller memoises on the state, exactly as the shelf's badges do.
 */
export function hotbarModel(state: GameState, side: Color): HotbarModel {
  const { position, industry } = state;
  const over = isGameOver(state.status);
  const yours = position.turn === side;
  const foundries = foundriesOf(state, side);
  const mintable = mintableFor(position, side);

  // Every forgeable rank, king included (§31 M4), in `MINTABLE`'s order.
  const types: PieceType[] = [...MINTABLE];

  // Asked once for the whole bar: the answer is a fact about the position, and
  // every tile filters against the same check.
  const breaksCheck = checkFilter(position, side);

  const forge: ForgeTile[] = types.map((type) => {
    // The union, built in ascending foundry order so the first claim on a
    // square is the lowest foundry's — which is what `leads` promises. The
    // check filter is applied *here*, before anything is derived from the set,
    // so `leads` and `shared` cannot describe a square that is not lit.
    const leads = new Map<number, number>();
    for (const foundry of foundries) {
      for (const at of fundableSquares(position, industry, foundry, side, type)) {
        if (breaksCheck && !breaksCheck(at, type)) continue;
        if (!leads.has(at)) leads.set(at, foundry);
      }
    }
    const squares = [...leads.keys()].sort((a, b) => a - b);
    // Rebuilt in square order: a `Map` iterates in insertion order, and two
    // readers walking one model must walk it the same way.
    const ordered = new Map<number, number>();
    const shared = new Map<number, number>();
    for (const at of squares) {
      ordered.set(at, leads.get(at) as number);
      const funders = fundersFor(position, industry, at, side).length;
      if (funders > 1) shared.set(at, funders);
    }

    const forgeable = mintable.includes(type);
    const reason = over
      ? "The game is over."
      : !forgeable
      ? "This army does not forge that."
      : !yours
      ? "Not your turn."
      : forgeRefusal(state, side) ??
        (squares.length === 0
      ? breaksCheck
        ? "In check — nothing you could forge would block it."
        : "No foundry coalition can pay for this."
      : null);

    return {
      type,
      ...billPrice(state, type),
      squares,
      leads: ordered,
      shared,
      forgeable,
      reason,
      role: PIECE_ROLE[type]
    };
  });

  const kinds = BUILD_KINDS;
  const reserved = reservedSquares(state);
  const emplaced = hasPower(industry, side, "emplacement");

  const builders = buildersOf(state, side);
  const build: BuildTile[] = kinds.map((kind) => {
    const leads = new Map<number, number>();
    // Only an obstruction stops a ray outright (§31 S1); any other building
    // is plowed, so it is never offered as a check block.
    const blocks = isObstructionKind(kind);
    let refused: BuildError | null = null;
    for (const foundry of builders) {
      // The foundry-shaped refusals once, rather than per square: nothing
      // above the square test differs between one of a foundry's neighbours
      // and the next, which is `fundableSquares`' own argument.
      const why = canBuild(position, industry, foundry, side, kind, undefined, reserved);
      if (why !== null) {
        if (refused === null || BUILD_ERROR_RANK.indexOf(why) > BUILD_ERROR_RANK.indexOf(refused)) {
          refused = why;
        }
        continue;
      }
      for (const at of buildSquares(position, industry, foundry, side, reserved, kind)) {
        // An obstruction stops a ray exactly as a piece standing on it does —
        // neither stops a knight. So a proxy piece of ours is an equivalent
        // blocker, and the rank is arbitrary: nothing asks what it attacks.
        if (breaksCheck && (!blocks || !breaksCheck(at, ROOK))) continue;
        if (!leads.has(at)) leads.set(at, foundry);
      }
    }
    const squares = [...leads.keys()].sort((a, b) => a - b);
    const ordered = new Map<number, number>();
    for (const at of squares) ordered.set(at, leads.get(at) as number);

    const reason = over
      ? "The game is over."
      : isTurretKind(kind) && !emplaced
      ? "Turrets need a Targeting Array."
      : !yours
      ? "Not your turn — building costs it."
      : squares.length === 0
      ? breaksCheck
        ? "In check — nothing you could build would block it."
        : refused !== null
        ? BUILD_SAYS[refused]
        : "No piece of yours stands beside bare ground with this in reach."
      : null;

    // The bill, not the stone count: the tile must quote what the reducer charges.
    const bill = structureBill(kind);
    return {
      kind,
      caption: bill
        .map(
          (row) =>
            `${row.count} ${materialName(row.line, row.grade).toLowerCase()}`
        )
        .join(" · "),
      rows: bill.map((row) => ({ count: row.count, line: row.line, grade: row.grade })),
      squares,
      leads: ordered,
      reason,
      role: STRUCTURE_ROLE[kind]
    };
  });

  const counts = new Map<StructureKind, number>();
  for (const kind of state.run?.tokens ?? []) counts.set(kind, (counts.get(kind) ?? 0) + 1);
  const tokens: TokenTile[] = [...counts.keys()]
    .sort((a, b) => a - b)
    .map((kind) => ({
      kind,
      count: counts.get(kind) as number,
      role: STRUCTURE_ROLE[kind]
    }));

  return { forge, build, tokens };
}
